import Vue from 'vue'
import qs from 'qs'

// Moldes da tipificação, apontados pelos templates do type via string-ponte
// (ex. categoryGiTemplate "Adult2" → molde kind=category name=Adult2); quem resolve
// apontador→molde é o mixin moldPointers, sobre o catálogo do kind.

// 50 é o máximo por página do core. Com authoring o catálogo cresce: a 51ª etiqueta de um kind
// desapareceria em silêncio do picker. Então pagina até esgotar.
const PAGE_SIZE = 50
const MAX_PAGES = 20

// Promessas em voo por kind, para não buscar o mesmo catálogo duas vezes
const inFlight = {}

// Incrementado a cada invalidação: identifica de qual geração do cache uma busca em voo é
let generation = 0

export const state = () => ({
  byKind: {},
  // Os kinds já carregados alguma vez. O `clearCache` não os zera: é por eles que o `refresh` sabe
  // o que recarregar depois que o `create` já esvaziou o cache.
  loadedKinds: [],
  loading: false
})

export const getters = {
  byKind(state) {
    return kind => state.byKind[kind] || []
  }
}

export const mutations = {
  loading(state, loading) {
    state.loading = loading
  },
  setForKind(state, { kind, molds }) {
    Vue.set(state.byKind, kind, molds)
    if (!state.loadedKinds.includes(kind)) state.loadedKinds.push(kind)
  },
  // Depois de escrever um molde o cache mentiria: a próxima leitura tem de bater no core. Invalida
  // os três kinds de propósito — renomear um molde muda a resolução dos apontadores de qualquer
  // kind —, e por isso quem chama tem de RECARREGAR o que estava carregado (ver `refresh`).
  clearCache(state) {
    state.byKind = {}
    generation += 1
    Object.keys(inFlight).forEach(kind => delete inFlight[kind])
  }
}

export const actions = {
  // Catálogo completo de um kind, para o authoring poder ESCOLHER um molde existente (e
  // pré-visualizar) em vez de o staff digitar a etiqueta de cabeça.
  fetchByKind({ state, commit }, kind) {
    if (state.byKind[kind]) {
      return state.byKind[kind]
    }

    // Os seis pickers montam juntos, antes de qualquer resposta chegar — sem guardar a promessa em
    // voo, o guard de cache acima não evita nada e cada kind é buscado duas vezes.
    if (inFlight[kind]) {
      return inFlight[kind]
    }

    commit('loading', true)
    // Token de geração: um fetch que já estava em voo quando o cache foi invalidado não pode
    // comitar o catálogo velho nem apagar o handle da busca nova. Quem incrementa é o
    // `clearCache` — aqui só se ANOTA a geração corrente. Incrementar aqui fazia cada busca
    // invalidar as irmãs: com os três kinds em paralelo, só o último comitava.
    const token = generation
    inFlight[kind] = (async () => {
      try {
        const molds = []
        let page = 1
        let received = PAGE_SIZE

        // Para quando a página vem incompleta: é o fim do catálogo. Guarda de sanidade em `page`
        // para um core que ignorasse a paginação não virar laço infinito.
        while (received === PAGE_SIZE && page <= MAX_PAGES) {
          const response = await this.$coreApi.get('staff/championship_molds', {
            params: {
              filter: { kind },
              page: { number: page, size: PAGE_SIZE }
            },
            paramsSerializer: params => qs.stringify(params)
          })

          received = response.data.data.length
          molds.push(...response.data.data)
          page += 1
        }

        if (token === generation) {
          commit('setForKind', { kind, molds })
        }

        return molds
      } finally {
        commit('loading', false)
        if (token === generation) {
          delete inFlight[kind]
        }
      }
    })()

    return inFlight[kind]
  },
  // Depois de criar um molde o catálogo está velho. `clearCache` derruba os TRÊS kinds, então
  // recarregar só o kind pedido deixava os outros pickers vazios — e picker vazio anuncia "não
  // existe molde com esta etiqueta" para apontador válido, convidando o staff a duplicar molde.
  refresh({ state, commit, dispatch }, kind) {
    const loaded = state.loadedKinds
    const kinds = !kind || loaded.includes(kind) ? loaded : [...loaded, kind]

    commit('clearCache')

    return Promise.all(kinds.map(one => dispatch('fetchByKind', one)))
  },
  async getById(_context, id) {
    const response = await this.$coreApi.$get(`staff/championship_molds/${id}`)

    return response.data
  },
  async create({ commit }, payload) {
    const response = await this.$coreApi.post('staff/championship_molds', payload)
    commit('clearCache')

    return response.data
  },
  // As linhas existentes voltam com o próprio `id` para o core atualizar por identidade, em vez de
  // apagar-e-recriar — os pisos de ranking são chaveados por linha e seriam levados junto.
  async update({ commit }, { id, ...payload }) {
    const response = await this.$coreApi.post(`staff/championship_molds/${id}/update`, payload)
    commit('clearCache')

    return response.data
  },
  async destroy({ commit }, id) {
    await this.$coreApi.delete(`staff/championship_molds/${id}`)
    commit('clearCache')
  }
}
